import { useMemo } from "react";
import { cn } from "../lib/classNames";

interface Props {
  /** ピン表示上限の現在値。dropdown の選択値。 */
  value: number;
  /** ピン表示上限の変更ハンドラ。 */
  onChange: (next: number) => void;
  /** dropdown の最小値。 */
  min: number;
  /** dropdown の最大値。 */
  max: number;
  /** 「ピン表示上限」テキストラベルを描画するか。アイコン代わりの可視ラベル。 */
  showLabel?: boolean;
}

/**
 * ピン表示上限の選択肢を 100 ステップで生成し、件数に応じた注意ラベルを付与する。
 *  100-400        : ラベルなし
 *  500-600        : 「重め」 (clusterer 描画がやや重くなる)
 *  700-1000       : 「重たい」 (初回 API バーストが大きく、ブラウザも重い)
 */
function buildPinCountOptions(min: number, max: number) {
  const options: Array<{ value: number; label: string }> = [];
  for (let v = min; v <= max; v += 100) {
    let suffix = "";
    if (v >= 700) suffix = " (重たい)";
    else if (v >= 500) suffix = " (重め)";
    options.push({ value: v, label: `${v}${suffix}` });
  }
  return options;
}

/**
 * ピン表示上限 select の単独コンポーネント。Header (PC) と FilterBar (SP) で共有する。
 * 値域に応じて border カラーを切り替え、件数が増えたときの警告をビジュアルで示す。
 */
export function PinCountSelect({
  value,
  onChange,
  min,
  max,
  showLabel = false,
}: Props) {
  const options = useMemo(() => buildPinCountOptions(min, max), [min, max]);
  const isHeavy = value >= 700;
  const isModerate = value >= 500 && value < 700;

  return (
    <label className="flex items-center gap-1.5 text-[11px] text-slate-400">
      {showLabel && <span>ピン表示上限</span>}
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          aria-label="マップに表示するピンの最大件数"
          className={cn(
            "appearance-none rounded-full border bg-slate-900/80 py-1 pl-2.5 pr-6 text-[11px] font-semibold text-slate-100 transition focus:outline-none focus:ring-2",
            isHeavy
              ? "border-rose-500/50 focus:ring-rose-400/40"
              : isModerate
                ? "border-amber-500/40 focus:ring-amber-400/40"
                : "border-white/15 focus:ring-white/30",
          )}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {/* select の右側に小さい矢印アイコン (appearance-none で消えるためカスタム) */}
        <svg
          width="9"
          height="9"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-300"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
    </label>
  );
}
